import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  LifeBuoy, Copy, Check, Shield, Webhook, KeyRound, ArrowUpRight,
  Code2, Zap,
} from "lucide-react";

const SNIPPETS = {
  curl: {
    create: (base) => `curl -X POST "${base}/api/public/tickets" \\
  -H "X-API-Key: pxk_live_YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "subject": "Website contact form",
    "message": "Hi team, my site is 502-ing since 10am.",
    "customer_name": "Jane Doe",
    "customer_email": "jane@example.com"
  }'`,
    status: (base) => `curl "${base}/api/public/tickets/TKT-ABC123" \\
  -H "X-API-Key: pxk_live_YOUR_KEY"`,
    reply: (base) => `curl -X POST "${base}/api/public/tickets/TKT-ABC123/replies" \\
  -H "X-API-Key: pxk_live_YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "body": "Any updates? Still down.",
    "author_name": "Jane Doe"
  }'`,
    attachment: (base) => `curl -X POST "${base}/api/public/tickets/TKT-ABC123/attachments" \\
  -H "X-API-Key: pxk_live_YOUR_KEY" \\
  -F "file=@./screenshot.png;type=image/png"`,
  },
  js: {
    create: (base) => `const res = await fetch("${base}/api/public/tickets", {
  method: "POST",
  headers: {
    "X-API-Key": process.env.PIXEL_API_KEY,
    "Content-Type": "application/json",
  },
  body: JSON.stringify({
    subject: "Website contact form",
    message: "Hi team, my site is 502-ing since 10am.",
    customer_name: "Jane Doe",
    customer_email: "jane@example.com",
  }),
});
const ticket = await res.json();
console.log(ticket.ticket_number);`,
    status: (base) => `const res = await fetch("${base}/api/public/tickets/TKT-ABC123", {
  headers: { "X-API-Key": process.env.PIXEL_API_KEY },
});
const ticket = await res.json();
console.log(ticket.status, ticket.replies.length);`,
    reply: (base) => `await fetch("${base}/api/public/tickets/TKT-ABC123/replies", {
  method: "POST",
  headers: {
    "X-API-Key": process.env.PIXEL_API_KEY,
    "Content-Type": "application/json",
  },
  body: JSON.stringify({ body: "Any updates?", author_name: "Jane Doe" }),
});`,
    verify: () => `import crypto from "node:crypto";

export function verifySignature(rawBody, signatureHeader, secret) {
  // signatureHeader = "sha256=…"
  const [algo, received] = (signatureHeader || "").split("=");
  if (algo !== "sha256") return false;
  const expected = crypto
    .createHmac("sha256", secret)
    .update(rawBody)
    .digest("hex");
  return crypto.timingSafeEqual(
    Buffer.from(received, "hex"),
    Buffer.from(expected, "hex"),
  );
}`,
  },
  py: {
    create: (base) => `import os, requests

r = requests.post(
    "${base}/api/public/tickets",
    headers={"X-API-Key": os.environ["PIXEL_API_KEY"]},
    json={
        "subject": "Website contact form",
        "message": "Hi team, my site is 502-ing since 10am.",
        "customer_name": "Jane Doe",
        "customer_email": "jane@example.com",
    },
    timeout=10,
)
r.raise_for_status()
print(r.json()["ticket_number"])`,
    status: (base) => `import os, requests

r = requests.get(
    "${base}/api/public/tickets/TKT-ABC123",
    headers={"X-API-Key": os.environ["PIXEL_API_KEY"]},
    timeout=10,
)
ticket = r.json()
print(ticket["status"], len(ticket["replies"]))`,
    reply: (base) => `import os, requests

requests.post(
    "${base}/api/public/tickets/TKT-ABC123/replies",
    headers={"X-API-Key": os.environ["PIXEL_API_KEY"]},
    json={"body": "Any updates?", "author_name": "Jane Doe"},
    timeout=10,
)`,
    verify: () => `import hmac, hashlib

def verify_signature(raw_body: bytes, signature_header: str, secret: str) -> bool:
    # signature_header = "sha256=…"
    if not signature_header or not signature_header.startswith("sha256="):
        return False
    received = signature_header.split("=", 1)[1]
    expected = hmac.new(secret.encode(), raw_body, hashlib.sha256).hexdigest()
    return hmac.compare_digest(received, expected)`,
  },
};

function CodeBlock({ code, lang = "bash", testId }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) {}
  };
  return (
    <div className="relative group">
      <pre
        data-testid={testId}
        className="bg-slate-900 text-slate-100 text-[12px] font-mono rounded-xl p-4 overflow-x-auto border border-slate-800">
        <code className={`language-${lang}`}>{code}</code>
      </pre>
      <button
        onClick={copy}
        type="button"
        className="absolute top-2 right-2 text-xs px-2 py-1 rounded-md border border-slate-700 bg-slate-800/80 text-slate-200 hover:bg-slate-700 opacity-0 group-hover:opacity-100 transition-opacity"
      >
        {copied ? <><Check className="inline w-3 h-3 mr-1 text-emerald-400" /> copied</>
                : <><Copy className="inline w-3 h-3 mr-1" /> copy</>}
      </button>
    </div>
  );
}

function LangTabs({ snippet, testIdBase }) {
  const [lang, setLang] = useState("curl");
  const base = typeof window !== "undefined" ? window.location.origin : "https://your-helpdesk";
  const code = snippet[lang] ? snippet[lang](base) : "";
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1" data-testid={`${testIdBase}-tabs`}>
        {["curl", "js", "py"].map((k) => (
          <button
            key={k}
            onClick={() => setLang(k)}
            data-testid={`${testIdBase}-tab-${k}`}
            className={`px-3 py-1 rounded-lg text-xs font-semibold border ${
              lang === k
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white border-slate-200 text-slate-600 hover:border-slate-400"
            }`}
          >
            {k === "curl" ? "curl" : k === "js" ? "Node.js" : "Python"}
          </button>
        ))}
      </div>
      <CodeBlock code={code} lang={lang === "curl" ? "bash" : lang === "js" ? "javascript" : "python"}
                 testId={`${testIdBase}-${lang}`} />
    </div>
  );
}

const FIELD_TABLE = (rows) => (
  <div className="overflow-x-auto rounded-xl border border-slate-200">
    <table className="min-w-full text-sm">
      <thead className="bg-slate-50 text-slate-500 text-[10px] font-mono uppercase tracking-widest">
        <tr>
          <th className="text-left px-3 py-2">Field</th>
          <th className="text-left px-3 py-2">Type</th>
          <th className="text-left px-3 py-2">Notes</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100 text-slate-700">
        {rows.map((r) => (
          <tr key={r.name}>
            <td className="px-3 py-2 font-mono text-[12px] text-slate-900">
              {r.name}
              {r.required && <span className="text-rose-500 ml-0.5">*</span>}
            </td>
            <td className="px-3 py-2 font-mono text-[12px] text-slate-500">{r.type}</td>
            <td className="px-3 py-2 text-[13px]">{r.notes}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function PublicApiDocs() {
  const base = typeof window !== "undefined" ? window.location.origin : "https://your-helpdesk";

  return (
    <div className="min-h-screen bg-slate-50">
      {/* lightweight hero / nav */}
      <div className="border-b border-slate-200 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" data-testid="docs-brand-link" className="flex items-center gap-2.5">
            <span className="w-8 h-8 rounded-xl bg-gradient-to-br from-blue-600 to-teal-500 grid place-items-center shadow-sm">
              <LifeBuoy className="w-4 h-4 text-white" />
            </span>
            <span className="font-display font-bold text-slate-900">Pixel Support</span>
            <span className="hidden sm:inline-block ml-1 text-[10px] font-mono uppercase tracking-widest text-slate-500 border border-slate-200 rounded px-1.5 py-0.5">
              API Docs
            </span>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/">
              <Button variant="outline">Back to app</Button>
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-12" data-testid="public-api-docs-page">
        {/* hero */}
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-blue-700 mb-2">Developer API · v1</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight leading-tight">
            Raise, read and reply to tickets from any external app.
          </h1>
          <p className="mt-3 text-slate-600 max-w-2xl">
            REST + JSON. Authenticated with per-integration API keys. Push-friendly via signed webhooks.
            Base URL: <code className="font-mono text-sm bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5">{base}</code>
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <a href="#create" className="beacon-badge bg-white border-slate-200 text-slate-700">Create ticket</a>
            <a href="#status" className="beacon-badge bg-white border-slate-200 text-slate-700">Fetch status</a>
            <a href="#reply"  className="beacon-badge bg-white border-slate-200 text-slate-700">Add reply</a>
            <a href="#attach" className="beacon-badge bg-white border-slate-200 text-slate-700">Attachments</a>
            <a href="#webhooks" className="beacon-badge bg-white border-slate-200 text-slate-700">Webhooks</a>
            <a href="#scopes"   className="beacon-badge bg-white border-slate-200 text-slate-700">Scopes</a>
          </div>
        </div>

        {/* auth */}
        <section className="beacon-card p-5 sm:p-7 space-y-3" id="auth">
          <div className="flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-blue-600" />
            <h2 className="font-display text-lg font-bold text-slate-900">Authentication</h2>
          </div>
          <p className="text-sm text-slate-700">
            Every request must include an <code>X-API-Key</code> header. Keys are issued from the admin panel
            at <Link to="/admin/api-keys" className="text-blue-700 hover:underline">Admin → API Keys</Link> and
            start with <code className="font-mono bg-slate-100 border border-slate-200 rounded px-1 py-0.5">pxk_live_</code>.
          </p>
          <CodeBlock testId="docs-auth-header"
                     code={`X-API-Key: pxk_live_WVemCLmCRNclKq9kGyq5xBCGcJzT1SxwqB32uauYI7o`} />
          <div className="text-xs text-slate-500 flex items-start gap-1.5">
            <Shield className="w-3.5 h-3.5 text-slate-400 mt-0.5" />
            Keys are stored as SHA-256 hashes only. We can't recover a lost key — rotate it from the admin page instead.
          </div>
        </section>

        {/* create */}
        <section className="space-y-3" id="create">
          <div className="flex items-center gap-2">
            <span className="beacon-badge bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">POST</span>
            <code className="font-mono text-sm text-slate-900">/api/public/tickets</code>
          </div>
          <h2 className="font-display text-lg font-bold text-slate-900">Create a ticket</h2>
          <p className="text-sm text-slate-700">Requires <strong>write</strong> scope. Returns the created ticket.</p>
          {FIELD_TABLE([
            { name: "subject",        type: "string",  required: true, notes: "3–200 chars." },
            { name: "message",        type: "string",  required: true, notes: "The customer's description. Up to 20,000 chars." },
            { name: "customer_name",  type: "string",  required: true, notes: "Display name of the end customer." },
            { name: "customer_email", type: "string (email)", required: true, notes: "Used for ack + reply emails." },
          ])}
          <LangTabs testIdBase="docs-create" snippet={{ curl: SNIPPETS.curl.create, js: SNIPPETS.js.create, py: SNIPPETS.py.create }} />
        </section>

        {/* status */}
        <section className="space-y-3" id="status">
          <div className="flex items-center gap-2">
            <span className="beacon-badge bg-blue-50 text-blue-700 border-blue-200 text-[10px]">GET</span>
            <code className="font-mono text-sm text-slate-900">/api/public/tickets/&#123;ticket_number&#125;</code>
          </div>
          <h2 className="font-display text-lg font-bold text-slate-900">Fetch ticket status + replies</h2>
          <p className="text-sm text-slate-700">
            Works with both <strong>read</strong> and <strong>write</strong> scopes. Returns the ticket and the full public reply thread (text only).
          </p>
          <LangTabs testIdBase="docs-status" snippet={{ curl: SNIPPETS.curl.status, js: SNIPPETS.js.status, py: SNIPPETS.py.status }} />
          <details className="text-sm">
            <summary className="cursor-pointer text-slate-600">Example response</summary>
            <CodeBlock testId="docs-status-response" code={`{
  "ticket_number": "TKT-ABC123",
  "subject": "Website contact form",
  "status": "in_progress",
  "priority": "medium",
  "customer_name": "Jane Doe",
  "customer_email": "jane@example.com",
  "assignee_name": "Kalyan Pramanik",
  "created_at": "2026-02-05T09:12:00+00:00",
  "updated_at": "2026-02-05T09:14:42+00:00",
  "replies": [
    { "author_name": "Kalyan Pramanik", "body": "Hi Jane, looking into it.",
      "source": "manual", "created_at": "2026-02-05T09:14:42+00:00" }
  ]
}`} />
          </details>
        </section>

        {/* reply */}
        <section className="space-y-3" id="reply">
          <div className="flex items-center gap-2">
            <span className="beacon-badge bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">POST</span>
            <code className="font-mono text-sm text-slate-900">/api/public/tickets/&#123;ticket_number&#125;/replies</code>
          </div>
          <h2 className="font-display text-lg font-bold text-slate-900">Add a customer reply</h2>
          <p className="text-sm text-slate-700">
            Requires <strong>write</strong>. Bumps the ticket and re-opens it if previously resolved/closed. The current assignee is notified by email.
          </p>
          {FIELD_TABLE([
            { name: "body",        type: "string", required: true,  notes: "The reply text. Up to 20,000 chars." },
            { name: "author_name", type: "string", required: false, notes: "Falls back to the ticket's customer_name." },
          ])}
          <LangTabs testIdBase="docs-reply" snippet={{ curl: SNIPPETS.curl.reply, js: SNIPPETS.js.reply, py: SNIPPETS.py.reply }} />
        </section>

        {/* attachment */}
        <section className="space-y-3" id="attach">
          <div className="flex items-center gap-2">
            <span className="beacon-badge bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">POST</span>
            <code className="font-mono text-sm text-slate-900">/api/public/tickets/&#123;ticket_number&#125;/attachments</code>
          </div>
          <h2 className="font-display text-lg font-bold text-slate-900">Attach a file</h2>
          <p className="text-sm text-slate-700">Multipart form upload (<code>file</code>). Max 10 MB per file. Requires <strong>write</strong>.</p>
          <LangTabs testIdBase="docs-attach"
                    snippet={{ curl: SNIPPETS.curl.attachment, js: () => "// use any FormData-aware HTTP client, e.g. fetch() with new FormData().", py: () => `# with requests
import os, requests
with open("./screenshot.png", "rb") as f:
    requests.post(
        "${base}/api/public/tickets/TKT-ABC123/attachments",
        headers={"X-API-Key": os.environ["PIXEL_API_KEY"]},
        files={"file": ("screenshot.png", f, "image/png")},
        timeout=20,
    )` }} />
        </section>

        {/* webhooks */}
        <section className="beacon-card p-5 sm:p-7 space-y-3" id="webhooks">
          <div className="flex items-center gap-2">
            <Webhook className="w-4 h-4 text-indigo-600" />
            <h2 className="font-display text-lg font-bold text-slate-900">Webhooks</h2>
          </div>
          <p className="text-sm text-slate-700">
            Attach a webhook URL to any API key (optional). We POST a JSON event to that URL whenever a relevant change happens, so you don't need to poll.
          </p>
          <div className="grid sm:grid-cols-3 gap-2">
            {[
              ["ticket.created",  "A new ticket was created."],
              ["ticket.updated",  "Status or assignee changed."],
              ["ticket.replied",  "A new public reply was added."],
            ].map(([n, d]) => (
              <div key={n} className="rounded-xl border border-slate-200 bg-white p-3">
                <code className="text-[12px] font-mono text-indigo-700">{n}</code>
                <div className="text-xs text-slate-600 mt-1">{d}</div>
              </div>
            ))}
          </div>
          <div className="text-sm text-slate-700 mt-2">
            Each delivery includes an <code>X-Pixel-Signature: sha256=…</code> header. Verify it with your <strong>webhook secret</strong> (shown once when you set the URL).
          </div>
          <details className="text-sm">
            <summary className="cursor-pointer text-slate-600">Example payload</summary>
            <CodeBlock testId="docs-webhook-payload" code={`POST https://your-app.com/pixel/webhook
Content-Type: application/json
X-Pixel-Event: ticket.updated
X-Pixel-Delivery: 94c3…
X-Pixel-Signature: sha256=7f1e…

{
  "event": "ticket.updated",
  "delivered_at": "2026-02-05T09:14:42+00:00",
  "ticket": {
    "ticket_number": "TKT-ABC123",
    "status": "in_progress",
    "priority": "medium",
    "customer_name": "Jane Doe",
    "customer_email": "jane@example.com",
    "assignee_id": "d2…",
    "created_at": "2026-02-05T09:12:00+00:00",
    "updated_at": "2026-02-05T09:14:42+00:00"
  },
  "changed_fields": ["status"],
  "previous": { "status": "open" }
}`} />
          </details>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1.5">Verify the signature</div>
            <LangTabs testIdBase="docs-verify"
                      snippet={{ curl: () => "# Use the Node.js or Python example in your real server code.", js: SNIPPETS.js.verify, py: SNIPPETS.py.verify }} />
          </div>
        </section>

        {/* scopes */}
        <section className="beacon-card p-5 sm:p-7 space-y-3" id="scopes">
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-amber-600" />
            <h2 className="font-display text-lg font-bold text-slate-900">Scopes</h2>
          </div>
          {FIELD_TABLE([
            { name: "read",  type: "scope", required: false, notes: "GET /api/public/tickets/{number} only. Safe for status widgets." },
            { name: "write", type: "scope", required: false, notes: "Everything above, plus POST endpoints (create ticket, reply, upload attachment)." },
          ])}
          <div className="text-sm text-slate-700">
            Mismatched scope → <code className="font-mono bg-slate-100 border border-slate-200 rounded px-1 py-0.5">403 Forbidden</code>.
            Missing / revoked key → <code className="font-mono bg-slate-100 border border-slate-200 rounded px-1 py-0.5">401 Unauthorized</code>.
          </div>
        </section>

        {/* errors */}
        <section className="space-y-3" id="errors">
          <div className="flex items-center gap-2">
            <Code2 className="w-4 h-4 text-rose-500" />
            <h2 className="font-display text-lg font-bold text-slate-900">Errors</h2>
          </div>
          {FIELD_TABLE([
            { name: "400", type: "validation", required: false, notes: "Payload is malformed or fails validation. Response has a `detail` string." },
            { name: "401", type: "auth",       required: false, notes: "Missing or invalid X-API-Key header." },
            { name: "403", type: "scope",      required: false, notes: "Key is read-only but tried a mutating endpoint." },
            { name: "404", type: "not found",  required: false, notes: "Unknown ticket number." },
            { name: "429", type: "rate limit", required: false, notes: "(reserved — not currently enforced)." },
          ])}
        </section>

        <div className="flex items-center justify-between pt-8 border-t border-slate-200">
          <div className="text-xs text-slate-500">Pixel Support · Public API v1</div>
          <Link to="/admin/api-keys" className="text-sm text-blue-700 hover:underline inline-flex items-center gap-1">
            Manage keys <ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </div>
  );
}
